import { JsonSchemaCache, JsonSchemaUtils, Objects } from "../../util";
import { OpenAPIV3 } from "../../@types";
import { DataMapperMember } from "./DataMapperMember";
import { DataMapperSelector } from "./DataMapperSelector";

/**
 * Defines a data mapper that maps data from a Data Source to Data Target.
 */
export class DataMapper<TSource, TTarget> {

  /** Unique ID or uri for the mapper */
  public uri: string;

  /** Description for the mapper: Shown in the selection list. */
  public title: string = null;

  /** Defines the data source */
  public source: DataMapperMember<TSource>;

  /** Defines the target for the mapping */
  public target: DataMapperMember<TTarget>;

  /** The actual map: The selectors that map from source to target. */
  public map: DataMapperSelector<TSource, TTarget>[];

  /**
   * If true, this is a list item mapper: Exportable.list() is used directly instead of
   * calling Exportable.getSingle() when exporting and export is done directly without wizard and preview.
   * RowType is also ApiListItem or derived from it.
   */
  public isSourceAList = false;

  /**
   * Constructor creates a new empty DataMapper.
   * You typically need to call at least setSchema() (source or target) and ten create the map using one of the methods.
   * @param uri Unique URI for the mapper
   * @param title Translated end-user title for the mapper
   */
  public constructor(uri: string, title: string) {
    this.uri = uri;
    this.title = title;
  }

  /** Resolves all the schemas in the selectors. */
  public resolve(): void {
    this.map.forEach((selector) => {
      selector.sourceProp = null;
      selector.targetProp = null;
      selector.resolve(this.source?.getSchema(), this.target?.getSchema());
    });
  }

  /**
   * Does the mapping from source to target.
   * @param sourceValue Optional source value that is mapped. If not specified, existing source.value is used.
   * @returns The results of the mapping.
   */
  public doMap(sourceValue: TSource[] = null): TTarget[] {
    this.target = this.target || new DataMapperMember();
    this.target.value = [];
    this.source = this.source || new DataMapperMember();
    if (sourceValue) {
      this.source.value = sourceValue;
    }
    this.source.value = this.source.value || [];
    this.source.value.forEach((sourceRow) => {
      const flattener = this.map.find((x) => x.flatten?.length);
      if (flattener) {
        // Only first level of flattening supported => Add hierarchy if necessary
        const flattenItems = Objects.getProperty(sourceRow, flattener.source) as any[];
        flattenItems.forEach((flattenRow) => {
          const targetRow: any = {};
          this.target.value.push(targetRow);
          this.map.forEach((x) => x.doMap(sourceRow, targetRow));
          flattener.flatten.forEach((x) => x.doMap(flattenRow, targetRow));
        });
      } else {
        const targetRow: any = {};
        this.target.value.push(targetRow);
        this.map.forEach((x) => x.doMap(sourceRow, targetRow));
      }
    });
    return this.target.value;
  }

  /**
   * Gets a simple key-to-key mapper for a schema object: no field conversion, just defining the columns and types.
   * @param schema The schema that defineses the columns for the mapping.
   * @param type The row type name (technical).
   */
  public setStringToSchemaMapping(schema: OpenAPIV3.NonArraySchemaObject, type: string): void {
    this.setSchema(schema, type, "target");
    const map = {};
    Object.keys(schema?.properties || {}).forEach((key) => {
      map[key] = key;
    });
    this.setPaths(map);
    // TODO: Add support for source type as string and type conversion => Use SxyGridController.convertTypes()
    this.generateSchema(type + "Import", "source");
  }

  /**
   * Sets a schema in one of the members using the schema cache.
   * Creates the member if none exists and also sets the isTable based on the schema.
   * @param schemaCache The schema cache from which the schema is fetched.
   * @param schemaDoc The URL for the Schema document that contains the schema
   * @param schemaName Name of the schema
   * @param member If set to "target", sets the target schema. Sets the source schema by default.
   */
  public async setSchemaFromCache(schemaCache: JsonSchemaCache, schemaDoc: string, schemaName: string, member: "source" | "target" = "source")
    : Promise<void> {
    await schemaCache.assureSchemaDocument(schemaDoc);
    const schema = schemaCache.findSchema(schemaDoc, schemaName) as OpenAPIV3.NonArraySchemaObject;
    this.setSchema(schema, schemaName, member);
  }

  /**
   * Sets the schema for the given member.
   * Creates the member if none exists and also sets the isTable based on the schema.
   * @param schema Schema to set into a member
   * @param schemaName Name of the schema
   * @param member Member to set.
   */
  public setSchema(schema: OpenAPIV3.NonArraySchemaObject, schemaName: string, member: "source" | "target" = "source"): void {
    let newMember: DataMapperMember<any>;
    if (member == "target") {
      newMember = this.target = this.target || new DataMapperMember();
    } else {
      newMember = this.source = this.source || new DataMapperMember();
    }
    newMember.schema = schema;
    newMember.name = schemaName;
    newMember.isTable = JsonSchemaUtils.isTable(schema);
  }

  /**
   * Generates one schema based on another using the paths as mappers.
   * @param name Name for the new schema
   * @param member The member that is being generated based on the other member.
   */
  public generateSchema(name: string, member: "source" | "target" = "target"): void {
    let srcMember: DataMapperMember<any>;
    let newMember: DataMapperMember<any>;
    if (member == "target") {
      this.target = this.target || new DataMapperMember<any>();
      newMember = this.target;
      srcMember = this.source;
    } else {
      this.source = this.source || new DataMapperMember<any>();
      newMember = this.source;
      srcMember = this.target;
    }
    newMember.name = name;
    newMember.isTable = true;
    newMember.schema = {
      description: `Schema ${name} generated based on ${srcMember.name} and paths mapper.`,
      type: "object",
      format: newMember.isTable ? "table" : newMember.name,
      properties: {},
    };
    this.map.forEach((selector) => {
      if (selector.flatten?.length) {
        if (member == "source") {
          throw new Error(`Only target generation supported for selectors with flatten ${name}.`);
        }
        selector.flatten.forEach((flattenSelector) => {
          flattenSelector.targetProp = flattenSelector.sourceProp;
          const flattenKey = flattenSelector.target || flattenSelector.source;
          if (flattenKey.indexOf(".") > -1) {
            (newMember.schema as any).sxyERROR = `Schema generation does not support hierarchical paths, e.g. ${flattenKey}. See console for all errors.`;
            console.error(`Schema generation (flatten) does not support hierarchical paths: ${flattenKey}`);
          } else {
            newMember.schema.properties[flattenKey] = flattenSelector.sourceProp?.schema;
          }
        });
      } else {
        if (member == "target") {
          selector.targetProp = selector.sourceProp;
        } else {
          selector.sourceProp = selector.targetProp;
        }
        const key = member == "target" ? selector.target : selector.source;
        if (key) {
          if (key.indexOf(".") > -1) {
            (newMember.schema as any).sxyERROR = `Schema generation does not support hierarchical paths, e.g. ${key}. See console for all errors.`;
            console.error(`Schema generation does not support hierarchical paths: ${key}`);
          } else {
            newMember.schema.properties[key] = selector.sourceProp?.schema; // Both source and target should be the same at this point.
            //x-salaxy- extensions
            if (selector.sourceProp) {
              newMember.schema.properties[key]["x-salaxy-title"] = `SALAXY.MODEL.${selector.sourceProp?.parentName}.${selector.sourceProp?.propertyName}.title`;
              newMember.schema.properties[key]["x-salaxy-description"] = `SALAXY.MODEL.${selector.sourceProp?.parentName}.${selector.sourceProp?.propertyName}.description`;
            }
          }
        }
      }
    });
  }

  /**
   * Sets selectors with paths to both target and source using a key-value pair object.
   * @param paths Paths as object where each key is a source path and value is
   * either a target path or selector (key is selector source if not set in the selector).
   */
  public setPaths(paths: { [key: string]: string | DataMapperSelector<any, any> }): void {
    // TODO: Support hierarchical paths object?
    if (!paths) {
      return;
    }
    this.map = [];
    Object.keys(paths).forEach((key) => {
      let selector = new DataMapperSelector();
      if (paths[key] && typeof paths[key] === "object") {
        selector = paths[key] as DataMapperSelector<any, any>;
      } else {
        selector.target = paths[key] as string;
      }
      selector.source = selector.source || key;
      this.map.push(selector);
    });
    if (!this.target) {
      // TODO: Support situation when both schemas are not known => optionally, guess the schema.
      this.target = new DataMapperMember();
    }
    this.resolve();
  }

  /**
   * Reverses the source / target.
   * Can be used for creating two mappers from the same definition: One for import, one for export.
   * @param uri Uri for the new reversed object.
   * @param title Treanslated end-user title for the new object.
   * @param isSourceAList If true, the reversed mapper is a list item mapper: Exportable.list() is used directly instead of
   * calling Exportable.getSingle() when exporting and export is done directly without wizard and preview.
   * RowType is also ApiListItem or derived from it.
   */
  public reverse(uri: string, title: string, isSourceAList = false): DataMapper<TTarget, TSource> {
    const result = new DataMapper<TTarget, TSource>(uri, title);
    result.target = this.source;
    result.source = this.target;
    result.isSourceAList = isSourceAList;
    result.map = this.reverseChildren(this.map || []);
    return result;
  }

  /**
   * Creates the Map based on source schema and property paths within that schema.
   * Also the target schema is created based these two.
   * Before calling this method, the source schema must be set or the method fails.
   * @param paths Array of paths that are used in creating the mapper
   * @param targetSchemaName Name for the new target schema.
   */
  public setPathsFromSourceSchema(paths: string[], targetSchemaName: string = null): void {
    if (!this.source?.schema) {
      throw new Error("Schema not set in setPathsFromSourceSchema().");
    }
    const name = targetSchemaName || (this.source.name + "_mapperTarget");
    const schema: OpenAPIV3.NonArraySchemaObject = {
      description: `Schema ${name} generated based on ${this.source.name} and paths mapper.`,
      type: "object",
      format: "table",
      properties: {},
    };
    this.map = [];
    paths.forEach((path) => {
      const selector = new DataMapperSelector();
      selector.source = path;
      const prop = JsonSchemaUtils.getProperty(this.source.schema, path);
      // TODO: Handle situation of duplicate headers (last part of the path).
      if (prop?.propertyName) {
        selector.target = prop.propertyName;
        schema.properties[prop.propertyName] = prop.schema;
        this.map.push(selector);
      }
    });
    this.setSchema(schema, name, "target");
    this.resolve();
  }

  private reverseChildren(originalChildren: DataMapperSelector<TSource, TTarget>[]): DataMapperSelector<TTarget, TSource>[] {
    return (originalChildren || []).map((orig) => {
      const inv = new DataMapperSelector<TTarget, TSource>();
      inv.func = null;
      inv.source = orig.target;
      inv.sourceProp = orig.targetProp;
      inv.target = orig.source;
      inv.targetProp = orig.sourceProp;
      if (orig.flatten?.length) {
        throw new Error("Not supported: Cannot reverse a DataMapperSelector with flatten");
      }
      return inv;
    });
  }
}
